import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-balham.css';
import { AgGridReact } from 'ag-grid-react';
import {
  TableContainer,
  TableContent,
  TableFooter,
  TableHeader
} from 'cdk-radial';
import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { setErrorMessages } from 'components/ErrorMessageHandler/slice';
import { ExportOrArchiveDialog } from 'components/ExportOrArchiveDialog';
import { PrintViewer } from 'components/PrintViewer/PrintViewer';
import { ScrollToTopButton } from 'components/ScrollToTopButton/ScrollToTopButton';
import { useDebounce } from 'hooks';
import { usePrintButton } from 'hooks/usePrintButton';
import { setPrintBtnDisabledOnRowFiltering } from 'pages/VendorsTablePage/components/PrintChecks/slice';
import { StyledLoader } from 'pages/VendorsTablePage/styled';
import { useAppDispatch } from 'store/hooks';
import {
  AG_GRID_THEME_CLASS,
  COMPONENT_MAIN_CLASS,
  DEFAULT_CELL_STYLE,
  ROW_SELECTION_MULTIPLE,
  TABLE_LAYOUT,
  TABLE_STYLES,
  HIDE_TABLE_BORDER_CLASS,
  PRINT_EXPORT_MAX_INVOICES
} from './constants';
import { messages } from './messages';
import { StyledTableHeaderCustomContent, StyledAggridHeader } from './styled';
import { ComposedTableDataType, TableProps } from './types';
import { excelStyles, filterRowData } from './utils';

export const SEARCH_VALUE_DEBOUNCE_TIME = 400;

export const ComposedTable = <
  TData extends ComposedTableDataType,
  TExportData extends ComposedTableDataType = Record<string, never>
>({
  columnDefs,
  customHeaderContent,
  customHeaderCount,
  customHeight,
  customWidth = TABLE_STYLES.width,
  defaultColDef,
  downloadDialogTitle,
  exportData,
  getCSVDocument,
  getExcelDocument,
  getGridApiParams = () => {},
  getPDFDocument,
  invoiceCount,
  isDownloadable = true,
  isExportDataFetching,
  isPrintable = true,
  isSearchable = true,
  onDebouncedSearchInputChange = () => {},
  onSearchInputChange = () => {},
  rowData,
  rowHeight = TABLE_STYLES.rowHeight,
  rowModelType,
  scrollToTopButtonShow = false,
  searchInputValue = '',
  showLoader = false,
  setFilteredVendorsRowData = () => {},
  setIsExportDataQueryEnabled,
  tableHeaderContent = undefined,
  tableHeaderCount,
  tableHeaderTitle,
  tableHeaderFilters,
  totalsInFooter,
  useCustomHeader,
  useCustomSearchFilter,
  hideTableBorder,
  ...props
}: TableProps<TData, TExportData>): React.JSX.Element => {
  const counter = tableHeaderCount || rowData?.length || 0;
  const { displayPrinter, onPrintClick, onClose } = usePrintButton();
  const [headerCount, setHeaderCount] = useState<number>(counter);
  const gridRef = useRef<AgGridReact<TData>>(null);
  const [displayMessage, setDisplayMessage] = useState<string>();
  const [isExportDialogOpen, setIsExportDialogOpen] = useState<boolean>(false);
  const tableClass = classNames(
    COMPONENT_MAIN_CLASS,
    AG_GRID_THEME_CLASS,
    hideTableBorder && HIDE_TABLE_BORDER_CLASS
  );
  const dispatch = useAppDispatch();
  const prevRowData = useRef(rowData);
  const intl = useIntl();

  const isNestedTable = useMemo(
    () => columnDefs?.some(({ rowGroup }) => rowGroup),
    [columnDefs]
  );

  const currentHeaderCount = customHeaderCount ?? headerCount;
  const tableTitle = `${tableHeaderTitle || ''} ${
    currentHeaderCount >= 0 ? `(${currentHeaderCount})` : ''
  }`;

  const allTableHeaders = { ...tableHeaderFilters, tableTitle };

  const debouncedSearchValue = useDebounce(
    searchInputValue,
    SEARCH_VALUE_DEBOUNCE_TIME
  );

  useEffect(() => {
    onDebouncedSearchInputChange(debouncedSearchValue);
    if (
      gridRef.current?.api &&
      !useCustomSearchFilter &&
      rowModelType !== 'serverSide'
    ) {
      gridRef.current.api.setGridOption(
        'quickFilterText',
        debouncedSearchValue
      );
      const { filteredRowCount, rowDataOfSearch } = filterRowData(
        gridRef.current.api,
        isNestedTable
      );
      setHeaderCount(filteredRowCount);
      setFilteredVendorsRowData(rowDataOfSearch);
    }
  }, [
    debouncedSearchValue,
    gridRef,
    isNestedTable,
    onDebouncedSearchInputChange,
    rowModelType,
    setFilteredVendorsRowData,
    useCustomSearchFilter
  ]);

  const onModelUpdated = useCallback(() => {
    if (gridRef.current?.api) {
      if (gridRef.current.api.getDisplayedRowCount()) {
        dispatch(setPrintBtnDisabledOnRowFiltering(false));
        gridRef.current.api.hideOverlay();
        setDisplayMessage(intl.formatMessage(messages.noRowsFound));
      } else {
        dispatch(setPrintBtnDisabledOnRowFiltering(true));
        gridRef.current.api.showNoRowsOverlay();
      }
    }
  }, [dispatch, gridRef, intl]);

  const handleDownloadClick = useCallback(() => {
    if (
      rowModelType !== 'serverSide' ||
      invoiceCount === undefined ||
      invoiceCount <= PRINT_EXPORT_MAX_INVOICES
    ) {
      setIsExportDialogOpen(true);
    } else {
      dispatch(
        setErrorMessages([intl.formatMessage(messages.exportInvoicesError)])
      );
    }
  }, [dispatch, intl, invoiceCount, rowModelType]);
  const handleDownloadClose = () => setIsExportDialogOpen(false);
  const handlePrintClick = useCallback(() => {
    if (
      rowModelType !== 'serverSide' ||
      invoiceCount === undefined ||
      invoiceCount <= PRINT_EXPORT_MAX_INVOICES
    ) {
      onPrintClick();
    } else {
      dispatch(
        setErrorMessages([intl.formatMessage(messages.printInvoicesError)])
      );
    }
  }, [dispatch, intl, invoiceCount, onPrintClick, rowModelType]);

  useEffect(() => {
    setHeaderCount(counter);
    onModelUpdated();
  }, [counter, onModelUpdated]);

  useEffect(() => {
    if (
      gridRef.current?.api &&
      prevRowData.current?.length !== rowData?.length
    ) {
      gridRef.current.api.setGridOption('rowData', []);
      if (rowData) {
        gridRef.current.api.setGridOption('rowData', rowData);
      }
    }
    prevRowData.current = rowData;
  }, [rowData, gridRef, prevRowData]);

  return (
    <StyledAggridHeader>
      <ExportOrArchiveDialog<TData, TExportData>
        exportData={exportData}
        getCSVDocument={
          getCSVDocument
            ? data => getCSVDocument(data, totalsInFooter, allTableHeaders)
            : undefined
        }
        getExcelDocument={
          getExcelDocument
            ? data => getExcelDocument(data, totalsInFooter, allTableHeaders)
            : undefined
        }
        getPDFDocument={
          getPDFDocument
            ? data =>
                getPDFDocument(
                  data,
                  gridRef,
                  true,
                  totalsInFooter,
                  allTableHeaders
                )
            : undefined
        }
        isExportDataFetching={isExportDataFetching}
        rowModelType={rowModelType}
        setIsExportDataQueryEnabled={setIsExportDataQueryEnabled}
        isOpen={isExportDialogOpen}
        downloadDialogTitle={downloadDialogTitle}
        onClose={handleDownloadClose}
        gridRef={gridRef}
        totalsInFooter={totalsInFooter}
        tableHeaderFilters={allTableHeaders}
        columnDefs={columnDefs}
      />
      <PrintViewer<TData, TExportData>
        exportData={exportData}
        getPDFDocument={
          getPDFDocument
            ? (data, convertToBlob) =>
                getPDFDocument(
                  data,
                  gridRef,
                  convertToBlob,
                  totalsInFooter,
                  allTableHeaders
                )
            : undefined
        }
        isExportDataFetching={isExportDataFetching}
        rowModelType={rowModelType}
        setIsExportDataQueryEnabled={setIsExportDataQueryEnabled}
        gridRef={gridRef}
        open={displayPrinter}
        onClose={onClose}
        totalsInFooter={totalsInFooter}
        tableHeaderFilters={allTableHeaders}
      />
      <TableContainer
        tableLayout={TABLE_LAYOUT.NORMAL}
        className={tableClass}
        style={{
          width: customWidth,
          height: customHeight || TABLE_STYLES.height
        }}
      >
        {!useCustomHeader ? (
          <>
            <TableHeader
              id="composed-table"
              headerTitle={tableTitle}
              isDownloadable={isDownloadable && currentHeaderCount !== 0}
              isPrintable={isPrintable && currentHeaderCount !== 0}
              isSearchable={isSearchable}
              searchInputValue={searchInputValue}
              onSearchInputChange={onSearchInputChange}
              searchInputName="tableSearchInputName"
              data-testid="table-header"
              onDownloadClick={handleDownloadClick}
              onPrintClick={handlePrintClick}
              downloadButtonText="export-composed-table"
              printButtonText="print-composed-table"
              hideBorder
            />
            <StyledTableHeaderCustomContent
              customHeaderContent={tableHeaderContent}
            />
          </>
        ) : (
          <>{customHeaderContent?.(gridRef)}</>
        )}
        {scrollToTopButtonShow ? <ScrollToTopButton /> : ''}
        {showLoader ? (
          <StyledLoader
            data-testid="loader"
            label={intl.formatMessage(messages.filterChipLoader)}
          />
        ) : (
          <TableContent data-testid="table-content" style={{ height: '600px' }}>
            <AgGridReact<TData>
              ref={gridRef}
              animateRows={false}
              columnDefs={columnDefs}
              defaultColDef={{
                ...defaultColDef,
                filter: true,
                cellStyle: DEFAULT_CELL_STYLE || ''
              }}
              rowData={rowData}
              excelStyles={excelStyles}
              pagination={false}
              overlayNoRowsTemplate={displayMessage}
              onGridReady={event => {
                getGridApiParams(event.api);
                event.api.sizeColumnsToFit();
              }}
              suppressPaginationPanel
              rowSelection={ROW_SELECTION_MULTIPLE}
              rowHeight={rowHeight}
              rowModelType={rowModelType}
              enableCellTextSelection
              stopEditingWhenCellsLoseFocus
              onModelUpdated={onModelUpdated}
              {...props}
            />
          </TableContent>
        )}
        <TableFooter hideBorder style={{ height: '0px' }} />
      </TableContainer>
    </StyledAggridHeader>
  );
};
